
// src/components/AdminPanel.js
import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver"; // ✅ added
import "./AdminPanel.css";

function AdminPanel() {
  const [activeTab, setActiveTab] = useState("admissions");

  // ─── State ───
  const [admissions, setAdmissions] = useState([]);
  const [news, setNews] = useState([]);
  const [results, setResults] = useState([]);
  const [ticker, setTicker] = useState([]);
  const [admissionsOpen, setAdmissionsOpen] = useState(true);

  // News
  const [newNews, setNewNews] = useState("");
  const [editNewsId, setEditNewsId] = useState(null);

  // Results
  const [resYear, setResYear] = useState("");
  const [resName, setResName] = useState("");
  const [resRoll, setResRoll] = useState("");
  const [resMarks, setResMarks] = useState("");
  const [editResultId, setEditResultId] = useState(null);

  // Ticker
  const [newTicker, setNewTicker] = useState("");
  const [editTickerId, setEditTickerId] = useState(null);

  const navigate = useNavigate();

  // ─────────────────────────────
  // Logout
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  // ─────────────────────────────
  // Firestore realtime listeners
  useEffect(() => {
    const unsubAdmissions = onSnapshot(collection(db, "admissions"), (snap) =>
      setAdmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubNews = onSnapshot(collection(db, "news"), (snap) =>
      setNews(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubResults = onSnapshot(collection(db, "results"), (snap) =>
      setResults(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubTicker = onSnapshot(collection(db, "ticker"), (snap) =>
      setTicker(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubSettings = onSnapshot(doc(db, "settings", "admissions"), (snap) => {
      if (snap.exists()) setAdmissionsOpen(snap.data().isOpen);
      else setDoc(doc(db, "settings", "admissions"), { isOpen: true });
    });

    return () => {
      unsubAdmissions();
      unsubNews();
      unsubResults();
      unsubTicker();
      unsubSettings();
    };
  }, []);

  // ─────────────────────────────
  // Toggle admissions open/close
  const toggleAdmissions = async () => {
    try {
      await setDoc(doc(db, "settings", "admissions"), { isOpen: !admissionsOpen });
      setAdmissionsOpen(!admissionsOpen);
    } catch (error) {
      console.error("Error toggling admissions:", error.message);
      alert("❌ Failed to update admissions status");
    }
  };

  // ─────────────────────────────
  // Export admissions to Excel (fixed for live hosting)
  const exportAdmissionsExcel = () => {
    if (admissions.length === 0) return alert("No admissions found!");

    const formattedData = admissions.map((a) => ({
      Name: a.name || "",
      Email: a.email || "",
      Phone: a.phone || "—",
      Course: a.course || "",
      Message: a.message || "",
      "Created At": a.createdAt?.toDate
        ? a.createdAt.toDate().toLocaleString()
        : "",
    }));

    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Admissions");

    // ✅ use file-saver for proper download
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "admissions.xlsx");
  };

  // ─────────────────────────────
  // Admissions delete
  const deleteAdmission = async (id) => {
    if (window.confirm("Are you sure you want to delete this admission?")) {
      try {
        await deleteDoc(doc(db, "admissions", id));
      } catch (error) {
        console.error("Error deleting admission:", error.message);
        alert("❌ Failed to delete admission");
      }
    }
  };

  // ─────────────────────────────
  // News
  const saveNews = async () => {
    if (!newNews.trim()) return alert("Enter news title");

    try {
      if (editNewsId) {
        await updateDoc(doc(db, "news", editNewsId), { title: newNews });
        setEditNewsId(null);
      } else {
        await addDoc(collection(db, "news"), {
          title: newNews,
          createdAt: serverTimestamp(),
        });
      }
      setNewNews("");
    } catch (error) {
      console.error("Error saving news:", error.message);
      alert("❌ Failed to save news");
    }
  };

  const editNewsItem = (n) => {
    setNewNews(n.title);
    setEditNewsId(n.id);
  };

  const deleteNews = async (id) => {
    if (window.confirm("Are you sure you want to delete this news?")) {
      try {
        await deleteDoc(doc(db, "news", id));
      } catch (error) {
        console.error("Error deleting news:", error.message);
        alert("❌ Failed to delete news");
      }
    }
  };

  // ─────────────────────────────
  // Results
  const saveResult = async () => {
    if (!resYear || !resName || !resRoll || !resMarks)
      return alert("Please fill all result fields");

    try {
      if (editResultId) {
        await updateDoc(doc(db, "results", editResultId), {
          year: resYear,
          name: resName,
          rollNumber: resRoll,
          marks: resMarks,
        });
        setEditResultId(null);
      } else {
        await addDoc(collection(db, "results"), {
          year: resYear,
          name: resName,
          rollNumber: resRoll,
          marks: resMarks,
          createdAt: serverTimestamp(),
        });
      }
      setResYear("");
      setResName("");
      setResRoll("");
      setResMarks("");
    } catch (error) {
      console.error("Error saving result:", error.message);
      alert("❌ Failed to save result");
    }
  };

  const editResult = (r) => {
    setResYear(r.year);
    setResName(r.name);
    setResRoll(r.rollNumber);
    setResMarks(r.marks);
    setEditResultId(r.id);
  };

  const deleteResult = async (id) => {
    if (window.confirm("Are you sure you want to delete this result?")) {
      try {
        await deleteDoc(doc(db, "results", id));
      } catch (error) {
        console.error("Error deleting result:", error.message);
        alert("❌ Failed to delete result");
      }
    }
  };

  // ─────────────────────────────
  // Ticker
  const saveTicker = async () => {
    if (!newTicker.trim()) return alert("Enter ticker text");

    try {
      if (editTickerId) {
        await updateDoc(doc(db, "ticker", editTickerId), { text: newTicker });
        setEditTickerId(null);
      } else {
        await addDoc(collection(db, "ticker"), {
          text: newTicker,
          createdAt: serverTimestamp(),
        });
      }
      setNewTicker("");
    } catch (error) {
      console.error("Error saving ticker:", error.message);
      alert("❌ Failed to save ticker");
    }
  };

  const editTickerItem = (t) => {
    setNewTicker(t.text);
    setEditTickerId(t.id);
  };

  const deleteTicker = async (id) => {
    if (window.confirm("Are you sure you want to delete this ticker?")) {
      try {
        await deleteDoc(doc(db, "ticker", id));
      } catch (error) {
        console.error("Error deleting ticker:", error.message);
        alert("❌ Failed to delete ticker");
      }
    }
  };

  // ─────────────────────────────
  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* ─── Tabs ─── */}
      <div className="tabs">
        <button onClick={() => setActiveTab("admissions")} className={activeTab === "admissions" ? "active" : ""}>Admissions</button>
        <button onClick={() => setActiveTab("news")} className={activeTab === "news" ? "active" : ""}>News</button>
        <button onClick={() => setActiveTab("results")} className={activeTab === "results" ? "active" : ""}>Results</button>
        <button onClick={() => setActiveTab("ticker")} className={activeTab === "ticker" ? "active" : ""}>Ticker</button>
      </div>

      {/* ───────────── Admissions ───────────── */}
      {activeTab === "admissions" && (
        <div className="section">
          <h3>Admissions ({admissions.length})</h3>
          <div className="button-group">
            <button className="toggle-btn" onClick={toggleAdmissions}>
              {admissionsOpen ? "Close Admissions" : "Open Admissions"}
            </button>
            <button className="export-btn" onClick={exportAdmissionsExcel}>Download Excel</button>
          </div>
          <ul>
            {admissions.map((a) => (
              <li key={a.id}>
                <div className="item-content">
                  <div>
                    <strong>Name:</strong> {a.name} <br />
                    <strong>Email:</strong> {a.email} <br />
                    <strong>Phone:</strong> {a.phone || "—"} <br />
                    <strong>Course:</strong> {a.course} <br />
                    <strong>Message:</strong> {a.message || "—"} <br />
                    <strong>Created At:</strong>{" "}
                    {a.createdAt?.toDate
                      ? a.createdAt.toDate().toLocaleString()
                      : "Not Available"}
                  </div>
                  <div className="action-buttons">
                    <button onClick={() => deleteAdmission(a.id)}>Delete</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ───────────── News ───────────── */}
      {activeTab === "news" && (
        <div className="section">
          <h3>Manage News ({news.length})</h3>
          <div className="add-box">
            <input type="text" placeholder="Enter news" value={newNews} onChange={(e) => setNewNews(e.target.value)} />
            <button onClick={saveNews}>{editNewsId ? "Update News" : "Add News"}</button>
          </div>
          <ul>
            {news.map((n) => (
              <li key={n.id}>
                <div className="item-content">
                  <div>
                    {n.title} <br />
                    <small>
                      {n.createdAt?.toDate
                        ? n.createdAt.toDate().toLocaleString()
                        : "No date"}
                    </small>
                  </div>
                  <div className="action-buttons">
                    <button onClick={() => editNewsItem(n)}>Edit</button>
                    <button onClick={() => deleteNews(n.id)}>Delete</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ───────────── Results ───────────── */}
      {activeTab === "results" && (
        <div className="section">
          <h3>Manage Results ({results.length})</h3>
          <div className="add-box">
            <input type="text" placeholder="Year" value={resYear} onChange={(e) => setResYear(e.target.value)} />
            <input type="text" placeholder="Student Name" value={resName} onChange={(e) => setResName(e.target.value)} />
            <input type="text" placeholder="Roll Number" value={resRoll} onChange={(e) => setResRoll(e.target.value)} />
            <input type="text" placeholder="Marks (e.g. 880/1100)" value={resMarks} onChange={(e) => setResMarks(e.target.value)} />
            <button onClick={saveResult}>{editResultId ? "Update Result" : "Add Result"}</button>
          </div>
          <div className="results-grid">
            {results.map((r) => (
              <div key={r.id} className="result-card">
                <div className="student-icon">🎓</div>
                <div className="result-info">
                  <h4>{r.name}</h4>
                  <p><strong>Roll:</strong> {r.rollNumber}</p>
                  <p><strong>Marks:</strong> {r.marks}</p>
                  <span className="result-year">{r.year}</span>
                </div>
                <div className="action-buttons" style={{ marginTop: "12px" }}>
                  <button onClick={() => editResult(r)}>Edit</button>
                  <button onClick={() => deleteResult(r.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Ticker ───────────── */}
      {activeTab === "ticker" && (
        <div className="section">
          <h3>Manage Ticker ({ticker.length})</h3>
          <div className="add-box">
            <input type="text" placeholder="Enter ticker text" value={newTicker} onChange={(e) => setNewTicker(e.target.value)} />
            <button onClick={saveTicker}>{editTickerId ? "Update Ticker" : "Add Ticker"}</button>
          </div>
          <ul>
            {ticker.map((t) => (
              <li key={t.id}>
                <div className="item-content">
                  <div>
                    {t.text} <br />
                    <small>
                      {t.createdAt?.toDate
                        ? t.createdAt.toDate().toLocaleString()
                        : "No date"}
                    </small>
                  </div>
                  <div className="action-buttons">
                    <button onClick={() => editTickerItem(t)}>Edit</button>
                    <button onClick={() => deleteTicker(t.id)}>Delete</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;

